import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader } from "@/components/Loader";
import { StatusChip } from "@/components/StatusChip";
import { ChevronLeft, KeyRound, Ban, Play, BadgeCheck } from "lucide-react";

const SERVICES = ["Caregiving"];

export default function NurseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [pw, setPw] = useState("");
  const [services, setServices] = useState([]);

  const load = async () => {
    try {
      const r = await api.get(`/admin/nurses/${id}`);
      setData(r.data);
      setServices(r.data.eligibility || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); } };
  const resetPw = () => {
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    act(() => api.post(`/admin/nurses/${id}/reset-password`, { new_password: pw }), "Password reset");
    setPw("");
  };
  const toggleService = (s) => setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  const saveEligibility = () => act(() => api.post(`/admin/nurses/${id}/eligibility`, { services }), "Eligibility updated");

  if (!data) return <Loader />;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="nurse-detail">
      <button onClick={() => navigate("/admin/nurses")} className="flex items-center gap-1 text-sm font-semibold text-[#003d9b]">
        <ChevronLeft className="w-4 h-4" /> Nurses
      </button>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-[32px] font-bold text-[#1c1b1b]">{data.name}</h1>
          <p className="text-sm text-[#434654]">{data.email} · {data.phone || "no phone"} · {data.city || "—"}</p>
        </div>
        <div className="flex gap-2">
          <StatusChip status={data.access_status} />
          <StatusChip status={data.account_status} />
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E5E7EB] font-semibold text-[#1c1b1b]">Module Progress & Video Watch Time</div>
        <table className="w-full">
          <thead><tr className="bg-[#fcf9f8] border-b border-[#E5E7EB]">
            {["Module", "Status", "PPT", "Video", "Watch (s)", "Score"].map((h) => (
              <th key={h} className="text-left text-[12px] font-bold uppercase tracking-wider text-[#434654] px-4 py-2">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {data.modules.map((m) => (
              <tr key={m.module_id} className="border-b border-[#E5E7EB] last:border-0">
                <td className="px-4 py-3 font-medium text-[#1c1b1b]">{m.title}</td>
                <td className="px-4 py-3"><StatusChip status={m.status} /></td>
                <td className="px-4 py-3 text-sm">{m.ppt_completed ? "✓" : "—"}</td>
                <td className="px-4 py-3 text-sm">{m.video_completed ? "✓" : "—"}</td>
                <td className="px-4 py-3 text-sm text-[#434654]">{m.video_watch_seconds}</td>
                <td className="px-4 py-3 text-sm font-semibold">{m.score != null ? `${m.score}%` : "—"}</td>
              </tr>
            ))}
            {data.modules.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-[#434654]">No modules assigned.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <p className="font-semibold text-[#1c1b1b] mb-3">Account</p>
          {data.account_status === "active" ? (
            <button data-testid="suspend-btn" onClick={() => act(() => api.post(`/admin/nurses/${id}/suspend`), "Nurse suspended")}
              className="w-full h-11 rounded-lg border border-[#ba1a1a]/30 text-[#ba1a1a] font-semibold flex items-center justify-center gap-2 hover:bg-red-50 transition-colors">
              <Ban className="w-4 h-4" /> Suspend
            </button>
          ) : (
            <button data-testid="reactivate-btn" onClick={() => act(() => api.post(`/admin/nurses/${id}/reactivate`), "Nurse reactivated")}
              className="w-full h-11 rounded-lg border border-[#004e33]/30 text-[#004e33] font-semibold flex items-center justify-center gap-2 hover:bg-green-50 transition-colors">
              <Play className="w-4 h-4" /> Reactivate
            </button>
          )}
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <p className="font-semibold text-[#1c1b1b] mb-3 flex items-center gap-2"><KeyRound className="w-4 h-4" /> Reset Password</p>
          <input data-testid="reset-password-input" type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password"
            className="h-11 w-full rounded-lg border border-[#E5E7EB] px-3 text-[15px] mb-2 focus:outline-none focus:ring-2 focus:ring-[#003d9b]" />
          <button data-testid="reset-password-btn" onClick={resetPw}
            className="w-full h-11 rounded-lg bg-[#003d9b] text-white font-semibold hover:bg-[#0052cc] transition-colors">Reset</button>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <p className="font-semibold text-[#1c1b1b] mb-3 flex items-center gap-2"><BadgeCheck className="w-4 h-4" /> Service Eligibility</p>
          <div className="space-y-1.5 mb-3 max-h-[140px] overflow-y-auto">
            {SERVICES.map((s) => (
              <label key={s} data-testid={`eligibility-${s.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-center gap-2 text-sm text-[#1c1b1b] cursor-pointer">
                <input type="checkbox" checked={services.includes(s)} onChange={() => toggleService(s)} className="w-4 h-4 accent-[#003d9b]" />
                {s}
              </label>
            ))}
          </div>
          <button data-testid="save-eligibility-btn" onClick={saveEligibility}
            className="w-full h-11 rounded-lg border border-[#E5E7EB] font-semibold text-[#003d9b] hover:bg-gray-50 transition-colors">Save Eligibility</button>
        </div>
      </div>
    </div>
  );
}
